import type { StageMode, StageNavAction } from "@msc/shared";

/**
 * Die zwei Entscheidungen der Cockpit-Steuerung, die nichts mit React zu tun
 * haben — bewusst hier als reine Funktionen, weil beide im laufenden Termin
 * auffliegen würden und sich in der Komponente nicht prüfen lassen:
 *
 *  - `isTypingTarget`: Wem gehört die Leertaste? Wer gerade eine Notiz
 *    tippt, blättert nicht weiter.
 *  - `planAdvance`: Was passiert beim „Weiter" — abhaken, kurz zeigen,
 *    dann umblättern.
 *  - `navActionForKey`: Welche Taste blättert — dieselbe Regel im Cockpit
 *    und im Kundenfenster.
 */

/**
 * Taste → Weiter/Zurück. Neben Pfeilen und Leertaste zählen Bild↓/Bild↑:
 * Genau die senden handelsübliche Präsentations-Clicker. Mit Strg, Alt oder
 * Cmd ist es ein Tastenkürzel des Browsers, keine Foliensteuerung.
 */
export function navActionForKey(e: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey">): StageNavAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") return "next";
  if (e.key === "ArrowLeft" || e.key === "PageUp") return "prev";
  return null;
}

/**
 * Verzögerung zwischen dem Abhaken und dem Umblättern. Die Bühne feiert das
 * Abhaken (`becameReviewed` in `routes/customer-view-stage.tsx`) nur, solange
 * das Ticket noch die aktuelle Folie ist — blättert das Cockpit im selben
 * Zug weiter, sieht der Kunde den Haken nie.
 */
export const CELEBRATE_DELAY_MS = 250;

/**
 * Tippt der Fokus gerade? Dann sind Pfeile und Leertaste Texteingabe und
 * nicht Foliensteuerung. Menüs und Dialoge zählen mit: Dort steuern die
 * Tasten das Bedienelement (Mantine-Menü, Statuswechsel-Dialog).
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return (
    target.closest(
      '[role="dialog"], [role="menu"], [role="menuitem"], [aria-haspopup], [data-menu-dropdown]',
    ) !== null
  );
}

export interface AdvancePlan {
  /** Ticket, das mit diesem „Weiter" abgehakt wird (null: keins). */
  markKey: string | null;
  /**
   * Erst den Haken zeigen (Snapshot mit unverändertem Index), dann umblättern.
   * Falsch, wenn es nichts zu feiern gibt — dann blättert das Cockpit sofort.
   */
  celebrateFirst: boolean;
}

/**
 * Was beim „Weiter" zu tun ist. Abgehakt wird nur das Ticket, das gerade auf
 * der Bühne liegt (`mode === "ticket"`), nur bei eingeschaltetem Auto-Haken
 * und nur, wenn es nicht ohnehin schon abgehakt ist — sonst wartet das
 * Cockpit auf einen Erledigt-Moment, den die Bühne gar nicht zeigt.
 */
export function planAdvance(o: {
  autoReview: boolean;
  mode: StageMode;
  deck: readonly string[];
  currentIndex: number;
  reviewedKeys: ReadonlySet<string>;
}): AdvancePlan {
  const leaving = o.mode === "ticket" ? o.deck[o.currentIndex] : undefined;
  if (!o.autoReview || leaving === undefined || o.reviewedKeys.has(leaving)) {
    return { markKey: null, celebrateFirst: false };
  }
  return { markKey: leaving, celebrateFirst: true };
}
